import type { SkillNodeDef } from "./schemas";
import { fillMissingPathNodes, LATE_UNLOCK_PATHS } from "./skill-path-fill";

const BASE_SKILL_NODES: SkillNodeDef[] = [
  // Hull
  {
    id: "reinforced_plating",
    name: "Reinforced Plating",
    description: "+15 max hull",
    branch: "hull",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["glass_hull"],
    maxStacks: 2,
    effects: { hullBonus: 15 },
  },
  {
    id: "ablative_armor",
    name: "Ablative Armor",
    description: "+25 armor, absorbs 30% damage before shield",
    branch: "hull",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { armorBonus: 25, armorAbsorbPct: 0.3 },
  },
  {
    id: "hull_weave_2",
    name: "Hull Weave II",
    description: "+8% max hull",
    branch: "hull",
    tier: 2,
    prerequisites: ["reinforced_plating"],
    incompatibleWith: [],
    maxStacks: 2,
    effects: { hullBonus: 8 },
  },
  {
    id: "emergency_seal",
    name: "Emergency Seal",
    description: "+10 max hull, +5% salvage",
    branch: "hull",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { hullBonus: 10, salvageMult: 1.05 },
  },
  {
    id: "glass_hull",
    name: "Glass Hull",
    description: "+20% damage, -15 max hull",
    branch: "hull",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["reinforced_plating"],
    maxStacks: 1,
    effects: { damageMult: 1.2, hullBonus: -15 },
  },
  {
    id: "composite_plating",
    name: "Composite Plating",
    description: "+20 max hull",
    branch: "hull",
    tier: 2,
    prerequisites: ["reinforced_plating"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { hullBonus: 20 },
  },
  // Shield
  {
    id: "capacitor_expansion",
    name: "Capacitor Expansion",
    description: "+12 max shield",
    branch: "shield",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["overcharge"],
    maxStacks: 2,
    effects: { shieldBonus: 12 },
  },
  {
    id: "fast_cycle",
    name: "Fast Cycle",
    description: "Shield regen starts 0.5s sooner",
    branch: "shield",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { shieldRegenDelayBonus: -0.5 },
  },
  {
    id: "overcharge",
    name: "Overcharge",
    description: "+25% max shield, -10 max hull",
    branch: "shield",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["capacitor_expansion"],
    maxStacks: 1,
    effects: { shieldMult: 1.25, hullBonus: -10 },
  },
  {
    id: "shield_weave_2",
    name: "Shield Weave II",
    description: "+10 max shield",
    branch: "shield",
    tier: 2,
    prerequisites: ["capacitor_expansion"],
    incompatibleWith: [],
    maxStacks: 2,
    effects: { shieldBonus: 10 },
  },
  {
    id: "hard_shell_skill",
    name: "Hard Shell",
    description: "+15% max shield",
    branch: "shield",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["glass_hull"],
    maxStacks: 1,
    effects: { shieldMult: 1.15 },
  },
  {
    id: "barrier_dense",
    name: "Dense Barrier",
    description: "+18 max shield",
    branch: "shield",
    tier: 2,
    prerequisites: ["hard_shell_skill"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { shieldBonus: 18 },
  },
  // Weapons
  {
    id: "hot_rounds",
    name: "Hot Rounds",
    description: "+10% weapon damage",
    branch: "weapons",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["coolant_gun"],
    maxStacks: 3,
    effects: { damageMult: 1.1 },
  },
  {
    id: "coolant_gun",
    name: "Coolant Gun",
    description: "-12% weapon cooldown, -5% damage",
    branch: "weapons",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["hot_rounds"],
    maxStacks: 1,
    effects: { cooldownMult: 0.88, damageMult: 0.95 },
  },
  {
    id: "ordnance_cache",
    name: "Ordnance Cache",
    description: "-20% ordnance cooldown",
    branch: "weapons",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { cooldownMult: 0.8 },
  },
  {
    id: "breach_tuned_skill",
    name: "Breach Tuned",
    description: "+15% weapon damage",
    branch: "weapons",
    tier: 2,
    prerequisites: ["hot_rounds"],
    incompatibleWith: ["coolant_gun"],
    maxStacks: 2,
    effects: { damageMult: 1.15 },
  },
  {
    id: "scatter_focus",
    name: "Scatter Focus",
    description: "+12% weapon damage",
    branch: "weapons",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 2,
    effects: { damageMult: 1.12 },
  },
  {
    id: "arc_overcharge",
    name: "Arc Overcharge",
    description: "+18% weapon damage",
    branch: "weapons",
    tier: 2,
    prerequisites: ["scatter_focus"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { damageMult: 1.18 },
  },
  // Mobility
  {
    id: "vector_trim",
    name: "Vector Trim",
    description: "+8% move speed",
    branch: "mobility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 2,
    effects: { speedMult: 1.08 },
  },
  {
    id: "boost_capacity",
    name: "Boost Capacity",
    description: "+20% boost fuel",
    branch: "mobility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 2,
    effects: { boostBonus: 0.2 },
  },
  {
    id: "nap_trim",
    name: "Assault Trim",
    description: "+10% move speed",
    branch: "mobility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { speedMult: 1.1 },
  },
  {
    id: "high_trim",
    name: "Velocity Trim",
    description: "+12% move speed",
    branch: "mobility",
    tier: 2,
    prerequisites: ["vector_trim"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { speedMult: 1.12 },
  },
  {
    id: "afterburn_skill",
    name: "Afterburn Trim",
    description: "+6% move speed, +10% boost fuel",
    branch: "mobility",
    tier: 2,
    prerequisites: ["boost_capacity"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { speedMult: 1.06, boostBonus: 0.1 },
  },
  {
    id: "drift_coupler",
    name: "Drift Coupler",
    description: "+5% move speed",
    branch: "mobility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 2,
    effects: { speedMult: 1.05 },
  },
  // Utility
  {
    id: "magnetic_sweep",
    name: "Magnetic Sweep",
    description: "+20% pickup radius",
    branch: "utility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { pickupRadiusMult: 1.2 },
  },
  {
    id: "salvage_tuned",
    name: "Salvage Tuned",
    description: "+15% salvage from drops",
    branch: "utility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["loot_courier_skill"],
    maxStacks: 2,
    effects: { salvageMult: 1.15 },
  },
  {
    id: "quick_extract",
    name: "Quick Extract",
    description: "+25% extract channel speed",
    branch: "utility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { extractBonus: 0.25 },
  },
  {
    id: "loot_courier_skill",
    name: "Loot Courier",
    description: "+25% salvage, -5% damage",
    branch: "utility",
    tier: 1,
    prerequisites: [],
    incompatibleWith: ["salvage_tuned"],
    maxStacks: 1,
    effects: { salvageMult: 1.25, damageMult: 0.95 },
  },
  {
    id: "beacon_boost",
    name: "Beacon Boost",
    description: "+35% extract channel speed",
    branch: "utility",
    tier: 2,
    prerequisites: ["quick_extract"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { extractBonus: 0.35 },
  },
  {
    id: "field_salvager",
    name: "Field Salvager",
    description: "+20% salvage from drops",
    branch: "utility",
    tier: 2,
    prerequisites: ["salvage_tuned"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { salvageMult: 1.2 },
  },
  // Keystones (max 1 per run)
  {
    id: "keystone_bulwark",
    name: "Bulwark Protocol",
    description: "+35 hull, +15 armor — fortress build",
    branch: "hull",
    tier: 3,
    kind: "keystone",
    prerequisites: ["reinforced_plating"],
    incompatibleWith: ["keystone_glass"],
    maxStacks: 1,
    effects: { hullBonus: 35, armorBonus: 15 },
  },
  {
    id: "keystone_glass",
    name: "Glass Cannon Doctrine",
    description: "+35% damage, -20 hull — glass build",
    branch: "weapons",
    tier: 3,
    kind: "keystone",
    prerequisites: ["hot_rounds"],
    incompatibleWith: ["keystone_bulwark"],
    maxStacks: 1,
    effects: { damageMult: 1.35, hullBonus: -20 },
  },
  {
    id: "keystone_momentum",
    name: "Momentum Core",
    description: "+18% speed, +10% boost",
    branch: "mobility",
    tier: 3,
    kind: "keystone",
    prerequisites: ["afterburn_skill"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { speedMult: 1.18, boostBonus: 0.1 },
  },
  {
    id: "keystone_capacitor",
    name: "Overcap Array",
    description: "+30 shield, faster regen",
    branch: "shield",
    tier: 3,
    kind: "keystone",
    prerequisites: ["capacitor_expansion"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { shieldBonus: 30, shieldRegenDelayBonus: -0.4 },
  },
  {
    id: "keystone_scavenger",
    name: "Field Scavenger",
    description: "+30% salvage, wider pickup",
    branch: "utility",
    tier: 3,
    kind: "keystone",
    prerequisites: ["salvage_tuned"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { salvageMult: 1.3, pickupRadiusMult: 1.25 },
  },
  // Intel nodes
  {
    id: "intel_threat_analyzer",
    name: "Threat Analyzer",
    description: "HUD shows live threat budget",
    branch: "utility",
    tier: 2,
    kind: "intel",
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { showThreatIntel: true },
  },
  {
    id: "intel_elite_ping",
    name: "Elite Ping",
    description: "+10% score vs elites",
    branch: "utility",
    tier: 2,
    kind: "intel",
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { eliteScoreMult: 1.1 },
  },
  {
    id: "intel_mine_sweeper",
    name: "Mine Sweeper",
    description: "See mine telegraphs 0.5s earlier",
    branch: "utility",
    tier: 2,
    kind: "intel",
    prerequisites: [],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { mineTelegraphEarlyS: 0.5 },
  },
  {
    id: "deep_nap",
    name: "Pressure Feed",
    description: "+8% damage",
    branch: "weapons",
    tier: 2,
    kind: "node",
    path: "nap",
    prerequisites: ["hot_rounds"],
    incompatibleWith: ["high_rider"],
    maxStacks: 1,
    effects: { damageMult: 1.08 },
  },
  {
    id: "high_rider",
    name: "Overdrive Rider",
    description: "+7% speed",
    branch: "mobility",
    tier: 2,
    kind: "node",
    path: "high",
    prerequisites: ["afterburn_skill"],
    incompatibleWith: ["deep_nap"],
    maxStacks: 1,
    effects: { speedMult: 1.07 },
  },
  {
    id: "ram_brace",
    name: "Ram Brace",
    description: "+12 hull, +8 armor",
    branch: "hull",
    tier: 2,
    path: "bulwark",
    prerequisites: ["ablative_armor"],
    incompatibleWith: ["glass_hull"],
    maxStacks: 1,
    effects: { hullBonus: 12, armorBonus: 8 },
  },
  {
    id: "glass_core",
    name: "Glass Core",
    description: "+12% damage, -8 hull",
    branch: "weapons",
    tier: 2,
    path: "glass",
    prerequisites: ["glass_hull"],
    incompatibleWith: ["reinforced_plating"],
    maxStacks: 1,
    effects: { damageMult: 1.12, hullBonus: -8 },
  },
  {
    id: "nap_hunter",
    name: "Close Hunter",
    description: "+7% damage",
    branch: "weapons",
    tier: 2,
    path: "nap",
    prerequisites: ["nap_trim"],
    incompatibleWith: ["high_rider"],
    maxStacks: 1,
    effects: { damageMult: 1.07 },
  },
  {
    id: "dust_keel",
    name: "Dust Keel",
    description: "+5% speed",
    branch: "mobility",
    tier: 1,
    path: "nap",
    prerequisites: [],
    incompatibleWith: ["sky_keel"],
    maxStacks: 1,
    effects: { speedMult: 1.05 },
  },
  {
    id: "sky_keel",
    name: "Vector Keel",
    description: "+5% speed",
    branch: "mobility",
    tier: 1,
    path: "high",
    prerequisites: [],
    incompatibleWith: ["dust_keel"],
    maxStacks: 1,
    effects: { speedMult: 1.05 },
  },
  {
    id: "thin_air",
    name: "Hot Vector",
    description: "+7% damage",
    branch: "weapons",
    tier: 2,
    path: "high",
    prerequisites: ["sky_keel"],
    incompatibleWith: ["deep_nap"],
    maxStacks: 1,
    effects: { damageMult: 1.07 },
  },
  {
    id: "magnet_well",
    name: "Magnet Well",
    description: "+18% pickup radius",
    branch: "utility",
    tier: 2,
    path: "scrapper",
    prerequisites: ["magnetic_sweep"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { pickupRadiusMult: 1.18 },
  },
  {
    id: "pad_thief",
    name: "Pad Thief",
    description: "+15% extract speed, +8% salvage",
    branch: "utility",
    tier: 2,
    path: "scrapper",
    prerequisites: ["quick_extract"],
    incompatibleWith: [],
    maxStacks: 1,
    effects: { extractBonus: 0.15, salvageMult: 1.08 },
  },
];

export const SKILL_NODES: SkillNodeDef[] = [
  ...BASE_SKILL_NODES,
  ...fillMissingPathNodes(BASE_SKILL_NODES),
];

export function skillNodeById(id: string): SkillNodeDef | undefined {
  return SKILL_NODES.find((n) => n.id === id);
}

export function countSkillNode(owned: readonly string[], nodeId: string): number {
  return owned.filter((id) => id === nodeId).length;
}

export function canAddSkillNode(
  owned: readonly string[],
  nextId: string,
  maxNodes: number,
): boolean {
  const next = skillNodeById(nextId);
  if (!next) return false;
  if (owned.length >= maxNodes) return false;
  if (owned.length === 0 && next.path && LATE_UNLOCK_PATHS.has(next.path)) return false;
  if (countSkillNode(owned, nextId) >= next.maxStacks) return false;
  if (next.kind === "keystone" && owned.some((id) => skillNodeById(id)?.kind === "keystone")) {
    return false;
  }
  if (!next.prerequisites.every((req) => owned.includes(req))) return false;
  return !owned.some((id) => next.incompatibleWith.includes(id));
}

export function eligibleSkillNodes(
  owned: readonly string[],
  maxNodes: number,
  banished: readonly string[] = [],
): SkillNodeDef[] {
  return SKILL_NODES.filter(
    (node) => !banished.includes(node.id) && canAddSkillNode(owned, node.id, maxNodes),
  );
}
